import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { ApiError } from "./api";
import { sessionKeys } from "./session";

/**
 * Every app's QueryClient: the shared defaults, and the sign-in page when the session ended outside this tab. That happens
 * when the person signs out of any Ghezaa app on this device or signs in as someone else (the device hint), is disabled,
 * or the session runs out: the next call answers 401, and re-asking the gate query shows the sign-in page at once instead
 * of error bands. The gate queries themselves (`/auth/me`, or an app's own `/api/me` given in `gateKeys`) are left alone,
 * so a 401 there cannot loop; `/auth/session` is re-asked too, so the sign-in page decides from a fresh answer.
 */
export function createAppQueryClient(gateKeys: readonly (readonly unknown[])[] = []): QueryClient {
  const gates = [sessionKeys.me, ...gateKeys];
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({ onError: (error, query) => signInOn401(error, query.queryKey) }),
    mutationCache: new MutationCache({ onError: (error) => signInOn401(error) }),
    defaultOptions: {
      queries: { retry: false, staleTime: 30_000, refetchOnWindowFocus: false },
    },
  });

  function signInOn401(error: unknown, queryKey?: readonly unknown[]) {
    if (!(error instanceof ApiError) || error.status !== 401) return;
    if (queryKey?.[0] === "auth" || gates.some((gate) => sameKey(gate, queryKey))) return;
    if (gates.some((gate) => client.getQueryState(gate)?.status === "error")) return;
    for (const gate of gates) void client.invalidateQueries({ queryKey: gate });
    void client.invalidateQueries({ queryKey: sessionKeys.session });
  }

  return client;
}

function sameKey(a: readonly unknown[], b: readonly unknown[] | undefined): boolean {
  return b !== undefined && a.length === b.length && a.every((part, i) => part === b[i]);
}
